import { log } from "./log";

// Fuente ÚNICA del mapeo país (nombre: "Chile"/"Perú"/"Uruguay", formato `clientes.pais`/
// `auth.pais`, regla 10.53) → código de 3 letras (formato `crm_actividades.pais`/`usuarios.pais` —
// CHI/PER/URU). Prohibido reimplementar este mapeo por componente (10.1, regla 10.268).
//
// Antecedente #1: el switch original (`VentasMiListaClientesV2`, copiado en `VentasMiListaClientes`
// legacy y `ListaClientes`) hacía `countryName.toLowerCase()` y comparaba contra `case` escritos
// con mayúscula ("Chile"/"Perú") — nunca matcheaba, caía a un `default` que devolvía el país SIN
// transformar. El filtro `pais=eq.${paisCode}` resultante jamás matcheaba nada, escondiendo
// actividad real de 61 clientes de Chile durante meses sin ningún error visible (10.177). Ver
// docs/decisiones/diagnostico-crm-actividades-directas-2026-09-20.md.
//
// Antecedente #2 (2026-09-21, regresión del fix de arriba): centralizar este util en
// `VentasMiListaClientesV2` arregló `crm_actividades` (necesita código) pero de paso empezó a
// pasarle el CÓDIGO a `getClientes`/`getIntermediarios`/`getOportunidades`/`getOportunidadesBTL`
// (necesitan NOMBRE) — 0 filas en las 4, CRM Chile roto por completo. Exactamente el mismo síntoma
// que la auditoría de las otras 2 copias predijo si se las llegara a "arreglar"
// (`docs/decisiones/auditoria-copias-getpaiscode-2026-09-21.md`). La causa de fondo: el switch
// original servía DOS grupos de consumidores distintos (nombre y código) con la MISMA variable —
// centralizar el mapeo no alcanza si el call-site sigue sin distinguir cuál necesita cada tabla.
// Ver `docs/decisiones/diagnostico-crm-actividades-directas-2026-09-20.md` (sección de regresión).
//
// Tabla verificada contra `docs/db/schema.md` + `SELECT DISTINCT pais` real (10.28,
// 2026-09-21) — antes de tocar un `pais` en código nuevo, buscá la tabla acá. Si no está,
// verificá con una query real, no asumas:
//
//   NOMBRE ("Chile"/"Perú"/"Uruguay", pasar `pais` tal cual):
//     clientes, intermediarios, sales, sales_btl, sales_contratos
//   CÓDIGO ("CHI"/"PER"/"URU", pasar `getPaisCode(pais)`):
//     crm_actividades, usuarios
//     (también: la edge function `calendar-events` — `listCalendarEvents`/`createCalendarEvent`/
//     `updateCalendarEvent`/`deleteCalendarEvent` — espera código, usa `TIME_ZONE[pais]`/
//     `getGoogleCredentials(pais)` internamente keyados CHI/PER)
//
// Este util normaliza (quita tildes + minúscula) ANTES de comparar, para no repetir ese error con
// una variante de acentuación o mayúsculas que hoy no ocurre pero que un `case` frágil no detecta.
const CODIGO_POR_PAIS_NORMALIZADO = {
  chile: "CHI",
  peru: "PER", // "Perú" sin tilde tras normalizar
  uruguay: "URU",
};

const CODIGOS_VALIDOS = new Set(Object.values(CODIGO_POR_PAIS_NORMALIZADO));

const normalizar = (str) => (str || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/**
 * Mapea el país de sesión ("Chile"/"Perú"/"Uruguay", con o sin tilde/mayúscula) al código de 3
 * letras que usan `crm_actividades`, `usuarios`, etc. (CHI/PER/URU). Idempotente: si ya llega un
 * código válido, lo devuelve igual. Ante país desconocido, loguea y devuelve `null` — nunca pasa el
 * string sin transformar (eso fue exactamente el bug: un filtro `pais=eq.<basura>` que no matchea
 * nada, sin ningún aviso — 10.170/10.177).
 * @param {string} pais
 * @returns {string|null}
 */
export const getPaisCode = (pais) => {
  if (CODIGOS_VALIDOS.has(pais)) return pais;
  const codigo = CODIGO_POR_PAIS_NORMALIZADO[normalizar(pais)];
  if (!codigo) {
    log.error("getPaisCode", `País desconocido: ${JSON.stringify(pais)} — devuelvo null controlado`);
    return null;
  }
  return codigo;
};
